/**
 * BulkApproveCTA - Bulk selection action bar
 *
 * Shows "Approve selected" / "Dismiss selected" options when items are selected.
 * Sticks to bottom of screen when scrolled out of view.
 * Only renders when items are selected.
 */

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Button } from '@/lib/base/buttons/button';

interface BulkApproveCTAProps {
  /** Number of selected actions */
  selectedCount?: number;
  /** Callback to approve selected actions */
  onApproveSelected?: () => void;
  /** Callback to dismiss selected actions */
  onDismissSelected?: () => void;
  /** Callback to clear selection */
  onClearSelection?: () => void;
}

/**
 * Bulk selection action bar
 *
 * Only appears when items are selected, showing approve/dismiss options.
 */
/** Selection bar content (only shown when items are selected) */
function SelectionBarContent({
  selectedCount,
  onApproveSelected,
  onDismissSelected,
  onClearSelection,
  isSticky = false,
}: {
  selectedCount: number;
  onApproveSelected?: () => void;
  onDismissSelected?: () => void;
  onClearSelection?: () => void;
  isSticky?: boolean;
}) {
  // Subtle brand tint composited over an OPAQUE token surface (no alpha on the bar).
  // Sticky version gets shadow for floating effect.
  const bgTint =
    'bg-[color-mix(in_srgb,var(--color-brand-600)_4%,var(--color-bg-primary))] ' +
    'dark:bg-[color-mix(in_srgb,var(--color-brand-600)_12%,var(--color-bg-secondary))]';
  const bgClass = isSticky ? `${bgTint} shadow-lg` : bgTint;

  return (
    <div
      className={`${bgClass} border border-secondary rounded-[3px] px-4 py-2.5`}
    >
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-2'>
          <span className='text-[14px] font-semibold text-primary'>
            {selectedCount} selected
          </span>
          <Button
            color='link-gray'
            onClick={onClearSelection}
            aria-label='Clear selection'
            className='!text-[14px] !font-medium'
          >
            Clear
          </Button>
        </div>
        <div className='flex items-center gap-3'>
          <Button
            color='link-gray'
            onClick={onDismissSelected}
            className='!text-[14px] !font-semibold'
          >
            Dismiss selected
          </Button>
          <Button color='primary' size='sm' onClick={onApproveSelected}>
            Approve selected
          </Button>
        </div>
      </div>
    </div>
  );
}

export function BulkApproveCTA({
  selectedCount = 0,
  onApproveSelected,
  onDismissSelected,
  onClearSelection,
}: BulkApproveCTAProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isSticky, setIsSticky] = useState(false);
  const [stickyRect, setStickyRect] = useState<{
    left: number;
    width: number;
  } | null>(null);

  // Store sticky state in ref to avoid re-subscription
  const isStickyRef = useRef(isSticky);

  useEffect(() => {
    isStickyRef.current = isSticky;
  }, [isSticky]);

  // Use IntersectionObserver to detect when selection bar goes out of view
  // Only applies when items are selected
  useEffect(() => {
    const container = containerRef.current;
    if (!container || selectedCount === 0) {
      setIsSticky(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        // When container is not intersecting viewport, show sticky bar
        const shouldBeSticky = !entry.isIntersecting;
        setIsSticky(shouldBeSticky);

        // Capture the position/size for sticky positioning
        if (shouldBeSticky) {
          const rect = container.getBoundingClientRect();
          setStickyRect({
            left: rect.left,
            width: rect.width,
          });
        }
      },
      {
        threshold: 0,
        rootMargin: '0px',
      }
    );

    observer.observe(container);

    // Also listen to resize events to update sticky rect
    const handleResize = () => {
      if (isStickyRef.current && container) {
        const rect = container.getBoundingClientRect();
        setStickyRect({
          left: rect.left,
          width: rect.width,
        });
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [selectedCount]); // isSticky removed from deps

  // Only render when items are selected
  if (selectedCount === 0) {
    return null;
  }

  return (
    <>
      {/* Inline selection bar — hidden from a11y tree when sticky version is active
          to prevent duplicate tab stops and screen reader announcements */}
      <div ref={containerRef} {...(isSticky ? { inert: true } : {})}>
        <SelectionBarContent
          selectedCount={selectedCount}
          onApproveSelected={onApproveSelected}
          onDismissSelected={onDismissSelected}
          onClearSelection={onClearSelection}
        />
      </div>

      {/* Sticky version at bottom of screen when scrolled out of view */}
      {isSticky &&
        stickyRect &&
        createPortal(
          <>
            <div role='status' aria-live='polite' className='sr-only'>
              {selectedCount} item{selectedCount !== 1 ? 's' : ''} selected –
              bulk actions available
            </div>
            <div
              className='fixed bottom-4 z-50'
              style={{
                left: stickyRect.left,
                width: stickyRect.width,
              }}
            >
              <SelectionBarContent
                selectedCount={selectedCount}
                onApproveSelected={onApproveSelected}
                onDismissSelected={onDismissSelected}
                onClearSelection={onClearSelection}
                isSticky
              />
            </div>
          </>,
          document.body
        )}
    </>
  );
}
